import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

/**
 * ONE primitive for Datadog global context, and the table of every dimension
 * it writes. Rules, and the reasoning behind them:
 * `.ai-context/telemetry-dimensions.md`.
 *
 * A dimension is an event-scoped attribute that attaches to every subsequent
 * RUM event and/or browser log line. It is NOT identity (`setUser`,
 * `setUserProperty` → `@usr.*`) and it is NOT emission (`addAction`,
 * `addError`); neither belongs here.
 *
 * Every variant a dimension can have — which sinks carry it, whether the
 * on-prem gate applies, what a nullish value means — is a FIELD in
 * `GLOBAL_DIMENSIONS` below, not a comment above a hand-rolled setter. That is
 * the whole point: a comment cannot be pinned by a test and the next dimension
 * copies it wrong.
 */

/** The two SDK global contexts. There is no third. */
export type DatadogSink = 'rum' | 'logs';

const BOTH_SINKS = ['rum', 'logs'] as const satisfies readonly DatadogSink[];
const RUM_ONLY = ['rum'] as const satisfies readonly DatadogSink[];

/**
 * What a nullish value means for one dimension. Three distinct behaviours,
 * and every dimension in the table picks one:
 *
 * - `remove` — `null`/`undefined` calls `removeGlobalContextProperty` on every
 *   sink, so an unstamped record is distinguishable from a stamped one. The
 *   default.
 * - `write` — the nullish value is passed through verbatim, OVERWRITING
 *   whatever was there. Reserved for dimensions whose consumers read the
 *   cleared key rather than its absence.
 * - `skip-empty` — nullish, or the empty string, writes NOTHING: no set, no
 *   remove. `0` and `false` are values and are written (a full volume is
 *   real). Absent means absent.
 */
export type NullishPolicy = 'remove' | 'write' | 'skip-empty';

/** Values the Datadog global context accepts from this app. */
export type GlobalDimensionValue = string | number | boolean | null | undefined;

export type GlobalDimensionSpec = {
  /** Which SDK global contexts carry this key. */
  readonly sinks: readonly DatadogSink[];
  /** Defaults to `'remove'`. */
  readonly onNullish?: NullishPolicy;
  /**
   * A second gate, applied after the on-prem one. `'web'` skips the write on
   * the desktop build. Defaults to `'any'`.
   */
  readonly platform?: 'any' | 'web';
  /**
   * Opting OUT of the on-prem gate. The string is the written justification
   * and is mandatory — a silent exemption is how an on-prem build starts
   * populating a buffer nobody audited.
   */
  readonly ungatedOnPremBecause?: string;
};

/**
 * The twenty-two dimensions `src/utils/tracing.ts` owns. Four more exist outside
 * it (`distribution_channel` / `mdm_provider` in
 * `src/lib/analytics/install-source-context.ts`, `desktop_version` /
 * `app_version` in `src/pages/_app.tsx`) and have not been registered here —
 * see the doc's "Not yet in the table".
 *
 * The prose for WHY each dimension exists stays on its exported setter in
 * `tracing.ts`. What lives here is only the mechanism.
 */
export const GLOBAL_DIMENSIONS = {
  // --- set once inside initDatadog(), which has already returned on-prem ---
  /** Desktop installer flavour, from `getDesktopInstallerType()`. */
  installer_type: { sinks: BOTH_SINKS },
  /** Joins FE↔BE telemetry on the `X-Device-Id` request header. */
  device_id: { sinks: BOTH_SINKS },
  /** Joins FE↔BE telemetry on the `X-Device-Session-Id` request header. */
  device_session_id: { sinks: BOTH_SINKS },
  /** Baked CI build SHA. Desktop-only; the caller omits it on web. */
  build_id: { sinks: BOTH_SINKS },
  /** Vercel deployment. Web-only; the caller omits it elsewhere. */
  deployment_id: { sinks: BOTH_SINKS },

  // --- set on auth resolution, from setDatadogProperties ---
  /**
   * Top-level `@user_ref_id`, matching the BE attribute — deliberately not
   * `@usr.*`.
   */
  user_ref_id: {
    sinks: BOTH_SINKS,
    ungatedOnPremBecause:
      'ungated today: setDatadogProperties is reached from auth.tsx / ' +
      'fhir-auth.tsx / tauriAuth.tsx with no isOnPrem guard anywhere on the ' +
      'path. Opting in would change the on-prem call sequence for no product ' +
      'benefit (RUM/Logs never init there, so the write lands in a pre-init ' +
      'buffer that never flushes). Gate it separately or never.',
  },
  /** The access token `sub` claim; identical to the BE value by construction. */
  idp_sub: {
    sinks: BOTH_SINKS,
    ungatedOnPremBecause:
      'ungated today, same path and same reasoning as user_ref_id above.',
  },

  // --- set post-login / post-IPC, from the exported setters ---
  /** Cleared by a null pass so a stale channel cannot outlive a region change. */
  update_channel: { sinks: BOTH_SINKS, onNullish: 'write' },
  /** Authenticated data-residency region. Slices telemetry by regional stack. */
  region: { sinks: BOTH_SINKS },
  /** Native host OS version. Distinct from `desktop_version`, the app semver. */
  desktop_os_version: { sinks: BOTH_SINKS },

  /**
   * Host machine capability facts. `skip-empty` because a field the machine
   * did not answer must produce NO key — a zero disk total on a dashboard
   * reads as a machine with no disk, which is a lie.
   *
   * `datadogLogs` is the load-bearing sink here, not `datadogRum`: forwarded
   * Rust log rows ARE browser log rows and inherit this Logs global context,
   * which is the only reason `device_id` has hardware beside it. Dropping
   * `'logs'` from these six leaves every dashboard looking right while the one
   * query they exist for comes back bare.
   */
  hw_cpu_model: { sinks: BOTH_SINKS, onNullish: 'skip-empty' },
  hw_cpu_physical_cores: { sinks: BOTH_SINKS, onNullish: 'skip-empty' },
  hw_cpu_logical_cores: { sinks: BOTH_SINKS, onNullish: 'skip-empty' },
  hw_mem_total_bytes: { sinks: BOTH_SINKS, onNullish: 'skip-empty' },
  hw_disk_total_bytes: { sinks: BOTH_SINKS, onNullish: 'skip-empty' },
  hw_disk_available_bytes: { sinks: BOTH_SINKS, onNullish: 'skip-empty' },

  /**
   * EHR launch profile. `write`, not `remove`: a later standalone session in
   * the same tab overwrites a stale profile.
   */
  ehr_profile_name: { sinks: BOTH_SINKS, onNullish: 'write' },

  /**
   * Audio-ledger rollout cohort. RUM only — the Logs half is bound inside
   * `@heidi/capture` on the `audio-recording` child logger, so it rides only
   * the records it can be joined to. Web-only rollout.
   */
  pipeline_mode: { sinks: RUM_ONLY, platform: 'web' },

  /** Resolved sections cohort. The OFF arm is stamped, not skipped. */
  sections_enabled: { sinks: BOTH_SINKS },
  sections_source: { sinks: BOTH_SINKS },

  /**
   * Routed note/document editor variant. `remove` on null (pane unmount,
   * PDF-only document) so an unstamped record is distinguishable from
   * `textarea`.
   */
  editor_variant: { sinks: BOTH_SINKS, onNullish: 'remove' },

  /**
   * Backend-resolved editor version (html/json/markdown) for an Athena user —
   * the one decision `useResolvedEditorVersion` exists to make (AME-1002 §3).
   * `remove` on null: a non-Athena user (or an unmounted observer) carries no
   * sectional version, so an unstamped record is distinguishable from `html`.
   * Fail-closed html (null/unrecognized/error on the wire) IS stamped `html` —
   * that is the editor the user gets; the cause is a separate `logger` event.
   */
  sectional_editor_version: { sinks: BOTH_SINKS, onNullish: 'remove' },
} as const satisfies Record<string, GlobalDimensionSpec>;

export type GlobalDimensionKey = keyof typeof GLOBAL_DIMENSIONS;

/**
 * The spec as the primitive reads it, with every default resolved. Exported so
 * the registry fixture pins what a declaration MEANS rather than what it omits.
 */
export type ResolvedGlobalDimensionSpec = {
  readonly sinks: readonly DatadogSink[];
  readonly onNullish: NullishPolicy;
  readonly platform: 'any' | 'web';
  readonly onPremGate: 'gated' | 'ungated';
  readonly ungatedOnPremBecause?: string;
};

export function resolveGlobalDimension(
  key: GlobalDimensionKey
): ResolvedGlobalDimensionSpec {
  const spec: GlobalDimensionSpec = GLOBAL_DIMENSIONS[key];
  return {
    sinks: spec.sinks,
    onNullish: spec.onNullish ?? 'remove',
    platform: spec.platform ?? 'any',
    onPremGate: spec.ungatedOnPremBecause === undefined ? 'gated' : 'ungated',
    ...(spec.ungatedOnPremBecause === undefined
      ? {}
      : { ungatedOnPremBecause: spec.ungatedOnPremBecause }),
  };
}

const writeToSink = (sink: DatadogSink, key: string, value: GlobalDimensionValue) => {
  if (sink === 'rum') {
    datadogRum.setGlobalContextProperty(key, value);
    return;
  }
  datadogLogs.setGlobalContextProperty(key, value);
};

const removeFromSink = (sink: DatadogSink, key: string) => {
  if (sink === 'rum') {
    datadogRum.removeGlobalContextProperty(key);
    return;
  }
  datadogLogs.removeGlobalContextProperty(key);
};

/**
 * Write one registered dimension to every sink its row names.
 *
 * ON-PREM-GATE: shared telemetry code emits through this cloud-only boundary.
 * A row may opt out only with a written reason (`ungatedOnPremBecause`).
 *
 * Conditional dimensions keep their `if` at the CALL SITE. Do not translate
 * `if (buildId) set(...)` into `set(key, buildId ?? null)`: an absent value is
 * never written today, and a nullish pass would issue a remove instead.
 */
export function setGlobalDimension(
  key: GlobalDimensionKey,
  value: GlobalDimensionValue
): void {
  const spec: GlobalDimensionSpec = GLOBAL_DIMENSIONS[key];

  if (isOnPrem && spec.ungatedOnPremBecause === undefined) return;
  if (spec.platform === 'web' && isTauri()) return;

  const onNullish = spec.onNullish ?? 'remove';
  const isNullish = value === null || value === undefined;

  if (onNullish === 'skip-empty') {
    if (isNullish || value === '') return;
  } else if (onNullish === 'remove' && isNullish) {
    spec.sinks.forEach((sink) => removeFromSink(sink, key));
    return;
  }

  spec.sinks.forEach((sink) => writeToSink(sink, key, value));
}
